import { useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useCollectionBinding,
  RichTextContent,
} from '@vibesites/runtime/react';
import {
  GalleryRow,
  GalleryLightbox,
  GallerySharedStyles,
  useGalleryHeadingColor,
  type GalleryImage,
} from './_galleryShared';

/**
 * Community-side of the photo gallery — renders the "Community Amenities"
 * grid (with its own lightbox). Photos are selected via the collection
 * binding's exact-match `filter` on `title === 'Community'`, so the
 * property's photo_gallery must categorize community shots with that
 * exact title.
 */
export function GalleryCommunity() {
  const headingColor = useGalleryHeadingColor();

  const communityImages = useCollectionBinding<GalleryImage>('gallerycommunity.images', {
    collection: 'photo_gallery',
    filter: { title: 'Community' },
  });

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  if (communityImages.length === 0) {
    return (
      <section className="bg-white">
        <div className="gallery-constrained py-12 text-center text-[var(--vibe-color-text-muted)]">
          <p className="font-body text-lg">Gallery images will be displayed here.</p>
        </div>
        <GallerySharedStyles />
      </section>
    );
  }

  return (
    <section className="bg-white">
      <div className="gallery-constrained pt-12 pb-8 md:pb-12">
        <GalleryRow
          heading={
            <RichTextContent
              path="gallerycommunity.heading"
              as="h2"
              className="gallery-row__heading"
              style={{ color: headingColor }}
              defaultValue="Community Amenities"
            />
          }
          images={communityImages}
          onSelect={setSelectedIndex}
        />
      </div>

      <GalleryLightbox
        images={communityImages}
        selectedIndex={selectedIndex}
        onClose={() => setSelectedIndex(null)}
        onChange={setSelectedIndex}
      />

      <GallerySharedStyles />
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'GalleryCommunity',
  displayName: 'Community Gallery',
  tokens: {
    'gallerycommunity.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: 'Community Amenities',
    },
  },
  collectionBindings: {
    'gallerycommunity.images': {
      label: 'Community Photos',
      collection: 'photo_gallery',
      filter: { title: 'Community' },
      description:
        'Pulled from the property photo gallery. Photos whose title is exactly "Community" appear in this grid.',
    },
  },
};

(GalleryCommunity as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default GalleryCommunity;
